(() => {
  'use strict';

  const ROOT_SELECTOR = 'au-cart[data-av-cart-section]';

  class AuCart extends HTMLElement {
    connectedCallback() {
      if (this.dataset.auCartReady === 'true' || this.dataset.avCartEmpty === 'true') return;

      this.dataset.auCartReady = 'true';
      this.dataset.auCartEnhanced = 'true';
      this.form = this.querySelector('[data-au-cart-form]');
      this.status = this.querySelector('[data-au-cart-status]');
      this.discountInput = this.querySelector('[data-au-discount-input]');
      this.discountStatus = this.querySelector('[data-au-discount-status]');

      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('change', (event) => this.onChange(event));
      this.discountInput?.addEventListener('input', () => this.clearDiscountError());
    }

    onClick(event) {
      const discountButton = event.target.closest('[data-au-discount-apply]');
      if (discountButton) {
        event.preventDefault();
        this.applyDiscount(discountButton);
        return;
      }

      const quantityButton = event.target.closest('[data-av-cart-qty]');
      if (quantityButton) {
        event.preventDefault();
        const line = quantityButton.closest('[data-av-cart-line]');
        const input = line?.querySelector('[data-av-cart-input]');
        if (!line || !input) return;
        const step = this.number(input.step, 1);
        const direction = quantityButton.dataset.avCartQty === 'plus' ? 1 : -1;
        this.requestQuantity(line, input, this.number(input.value, 1) + (step * direction), quantityButton.dataset.avCartQty);
        return;
      }

      const removeLink = event.target.closest('.av-cart__remove');
      if (this.dataset.auCartEnhanced !== 'true') return;
      if (removeLink && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
        const line = removeLink.closest('[data-av-cart-line]');
        if (!line) return;
        event.preventDefault();
        this.changeLine(line, 0, 'remove');
      }
    }

    onChange(event) {
      if (this.dataset.auCartEnhanced !== 'true') return;
      const input = event.target.closest('[data-av-cart-input]');
      if (!input) return;
      const line = input.closest('[data-av-cart-line]');
      if (!line) return;
      this.requestQuantity(line, input, this.number(input.value, this.number(line.dataset.itemQuantity, 1)), 'input');
    }

    requestQuantity(line, input, requested, focusKind) {
      const minimum = this.number(input.min, 1);
      const maximum = input.max === '' ? null : this.number(input.max, null);
      const current = this.number(line.dataset.itemQuantity, 1);
      let quantity = Math.max(minimum, requested);

      if (maximum !== null && quantity > maximum) {
        quantity = maximum;
        input.value = String(maximum);
        this.showLineError(line, this.dataset.auCartLimitMessage.replace('__quantity__', String(maximum)));
      } else {
        this.showLineError(line, '');
      }

      if (quantity === current) {
        input.value = String(quantity);
        input.focus();
        return;
      }

      this.changeLine(line, quantity, focusKind);
    }

    async changeLine(line, quantity, focusKind) {
      if (this.getAttribute('aria-busy') === 'true') return;
      const lineNumber = this.number(line.dataset.line, 0);
      const lineKey = line.dataset.key || '';
      if (!lineNumber) return;

      this.setBusy(true);
      this.setStatus('');

      try {
        const response = await fetch(this.dataset.auCartChangeUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: lineNumber, quantity })
        });
        const cart = await response.json();
        if (!response.ok) throw new Error('cart_change_failed');
        await this.refresh(cart.item_count, { lineKey, focusKind, removed: quantity === 0 });
      } catch (error) {
        this.setBusy(false);
        this.removeAttribute('data-au-cart-enhanced');
        this.showLineError(line, this.dataset.auCartErrorMessage);
        const input = line.querySelector('[data-av-cart-input]');
        input?.focus();
      }
    }

    async applyDiscount(button) {
      const code = this.discountInput?.value.trim();
      if (!code || this.getAttribute('aria-busy') === 'true') {
        this.discountInput?.focus();
        return;
      }

      this.setBusy(true);
      button.disabled = true;
      this.clearDiscountError();

      try {
        const cartResponse = await fetch(`${this.dataset.auCartUrl}.js`, { headers: { Accept: 'application/json' } });
        const currentCart = await cartResponse.json();
        if (!cartResponse.ok) throw new Error('cart_read_failed');

        const existingCodes = Array.isArray(currentCart.discount_codes)
          ? currentCart.discount_codes.filter((entry) => entry.applicable && entry.code).map((entry) => entry.code)
          : [];
        const discount = [...new Set([...existingCodes, code])].join(',');
        const response = await fetch(this.dataset.auCartUpdateUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount })
        });
        const cart = await response.json();
        if (!response.ok) throw new Error('discount_update_failed');

        const applied = Array.isArray(cart.discount_codes) && cart.discount_codes.some((entry) => (
          entry.applicable && String(entry.code).toLocaleUpperCase() === code.toLocaleUpperCase()
        ));

        if (!applied) {
          this.showDiscountError(this.dataset.auCouponInvalidMessage);
          return;
        }

        this.discountStatus.textContent = this.dataset.auCouponAppliedMessage;
        await this.refresh(cart.item_count, { coupon: true });
      } catch (error) {
        this.showDiscountError(this.dataset.auCouponErrorMessage);
      } finally {
        this.setBusy(false);
        button.disabled = false;
      }
    }

    async refresh(cartCount, focus = {}) {
      const url = new URL(this.dataset.auCartUrl, window.location.origin);
      url.searchParams.set('section_id', this.dataset.auSectionId);
      const response = await fetch(url.toString(), { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('cart_section_failed');

      const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextRoot = documentFragment.querySelector(ROOT_SELECTOR);
      if (!nextRoot) throw new Error('cart_section_missing');

      this.replaceWith(nextRoot);
      document.dispatchEvent(new CustomEvent('theme:cart:change', {
        bubbles: true,
        detail: { cartCount: this.number(cartCount, 0) }
      }));

      window.requestAnimationFrame(() => {
        if (focus.lineKey && !focus.removed) {
          const nextLine = [...nextRoot.querySelectorAll('[data-av-cart-line]')].find((candidate) => candidate.dataset.key === focus.lineKey);
          const target = focus.focusKind === 'input'
            ? nextLine?.querySelector('[data-av-cart-input]')
            : nextLine?.querySelector(`[data-av-cart-qty="${focus.focusKind}"]`);
          target?.focus();
        } else if (focus.removed) {
          nextRoot.querySelector('h1')?.focus();
        } else if (focus.coupon) {
          const status = nextRoot.querySelector('[data-au-discount-status]');
          if (status) status.textContent = nextRoot.dataset.auCouponAppliedMessage;
          status?.focus();
        }
      });
    }

    clearDiscountError() {
      if (!this.discountInput || !this.discountStatus) return;
      this.discountInput.removeAttribute('aria-invalid');
      this.discountStatus.textContent = '';
    }

    showDiscountError(message) {
      this.setBusy(false);
      this.discountInput?.setAttribute('aria-invalid', 'true');
      if (this.discountStatus) this.discountStatus.textContent = message;
      this.discountStatus?.focus();
    }

    showLineError(line, message) {
      const error = line.querySelector('[data-au-cart-line-error]');
      if (error) error.textContent = message;
    }

    setStatus(message) {
      if (this.status) this.status.textContent = message;
    }

    setBusy(busy) {
      this.setAttribute('aria-busy', String(busy));
    }

    number(value, fallback) {
      const parsed = Number.parseInt(value, 10);
      return Number.isFinite(parsed) ? parsed : fallback;
    }
  }

  if (!customElements.get('au-cart')) customElements.define('au-cart', AuCart);
})();
